import { Column, Row, Text } from '@amzn/meridian';
import desktopTokens from '@amzn/meridian-tokens/base/icon/desktop';
import phoneMobileTokens from '@amzn/meridian-tokens/base/icon/phone-mobile';
import type { FC, ReactNode } from 'react';
import { css, LANGUAGE_COLORS, LOCALES } from './constants';
import { LocaleRow } from './LocaleRow';
import { MarketplaceBox } from './MarketplaceBox';

const PreviewWrapper: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <div className={css.previewContainer}>
    <div className={css.flexSpacer} />
    <Column spacing="200" width="100%">
      <Text color="secondary">EXAMPLE</Text>
      <Column height="300" spacing="300">
        {children}
      </Column>
    </Column>
  </div>
);

export const AllPlatformsPreview: FC = () => (
  <PreviewWrapper>
    <Row width="100%">
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.teal.backgroundColor} borderColor={LANGUAGE_COLORS.teal.borderColor}>
        <Row>
          <LocaleRow icon={desktopTokens} text="Desktop" />
          <LocaleRow icon={phoneMobileTokens} text="Mobile" />
        </Row>
      </MarketplaceBox>
    </Row>
  </PreviewWrapper>
);

export const SeparatePlatformsPreview: FC = () => (
  <PreviewWrapper>
    <Row spacing="300" width="100%">
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.pink.backgroundColor} borderColor={LANGUAGE_COLORS.pink.borderColor}>
        <LocaleRow icon={desktopTokens} text="Desktop" />
      </MarketplaceBox>
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.teal.backgroundColor} borderColor={LANGUAGE_COLORS.teal.borderColor}>
        <LocaleRow icon={phoneMobileTokens} text="Mobile" />
      </MarketplaceBox>
    </Row>
  </PreviewWrapper>
);

export const AllMarketplacesPreview: FC = () => (
  <PreviewWrapper>
    <MarketplaceBox backgroundColor={LANGUAGE_COLORS.teal.backgroundColor} borderColor={LANGUAGE_COLORS.teal.borderColor}>
      <Row>
        <Column spacingInset="100" width="50%">
          <LocaleRow countryCode={LOCALES.US_ENGLISH.countryCode} text={LOCALES.US_ENGLISH.text} />
          <LocaleRow countryCode={LOCALES.US_SPANISH.countryCode} text={LOCALES.US_SPANISH.text} />
        </Column>
        <Column spacingInset="100" width="50%">
          <LocaleRow countryCode={LOCALES.CA_ENGLISH.countryCode} text={LOCALES.CA_ENGLISH.text} />
          <LocaleRow countryCode={LOCALES.CA_FRENCH.countryCode} text={LOCALES.CA_FRENCH.text} />
        </Column>
      </Row>
    </MarketplaceBox>
  </PreviewWrapper>
);

export const PerMarketplacePreview: FC = () => (
  <PreviewWrapper>
    <Row
      spacing="300"
      widths={[
        '50%',
        '50%',
      ]}
    >
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.pink.backgroundColor} borderColor={LANGUAGE_COLORS.pink.borderColor}>
        <Column spacingInset="100">
          <LocaleRow countryCode={LOCALES.US_ENGLISH.countryCode} text={LOCALES.US_ENGLISH.text} />
          <LocaleRow countryCode={LOCALES.US_SPANISH.countryCode} text={LOCALES.US_SPANISH.text} />
        </Column>
      </MarketplaceBox>
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.teal.backgroundColor} borderColor={LANGUAGE_COLORS.teal.borderColor}>
        <Column spacingInset="100">
          <LocaleRow countryCode={LOCALES.CA_ENGLISH.countryCode} text={LOCALES.CA_ENGLISH.text} />
          <LocaleRow countryCode={LOCALES.CA_FRENCH.countryCode} text={LOCALES.CA_FRENCH.text} />
        </Column>
      </MarketplaceBox>
    </Row>
  </PreviewWrapper>
);

export const SameLanguagePreview: FC = () => (
  <PreviewWrapper>
    <MarketplaceBox backgroundColor={LANGUAGE_COLORS.teal.backgroundColor} borderColor={LANGUAGE_COLORS.teal.borderColor}>
      <Row
        widths={[
          '51%',
          '50%',
        ]}
      >
        <Column alignmentVertical="center">
          <LocaleRow countryCode={LOCALES.US_ENGLISH.countryCode} text={LOCALES.US_ENGLISH.text} />
        </Column>
        <Column>
          <LocaleRow countryCode={LOCALES.CA_ENGLISH.countryCode} text={LOCALES.CA_ENGLISH.text} />
        </Column>
      </Row>
    </MarketplaceBox>

    <Row
      spacing="300"
      widths={[
        '50%',
        '50%',
      ]}
    >
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.pink.backgroundColor} borderColor={LANGUAGE_COLORS.pink.borderColor}>
        <Column alignmentHorizontal="stretch">
          <LocaleRow countryCode={LOCALES.US_SPANISH.countryCode} text={LOCALES.US_SPANISH.text} />
        </Column>
      </MarketplaceBox>

      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.orange.backgroundColor} borderColor={LANGUAGE_COLORS.orange.borderColor}>
        <Column>
          <LocaleRow countryCode={LOCALES.CA_FRENCH.countryCode} text={LOCALES.CA_FRENCH.text} />
        </Column>
      </MarketplaceBox>
    </Row>
  </PreviewWrapper>
);

export const NoSharingPreview: FC = () => (
  <PreviewWrapper>
    <Row
      spacing="300"
      widths={[
        '50%',
        '50%',
      ]}
    >
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.pink.backgroundColor} borderColor={LANGUAGE_COLORS.pink.borderColor}>
        <Column>
          <LocaleRow countryCode={LOCALES.US_ENGLISH.countryCode} text={LOCALES.US_ENGLISH.text} />
        </Column>
      </MarketplaceBox>
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.purple.backgroundColor} borderColor={LANGUAGE_COLORS.purple.borderColor}>
        <Column>
          <LocaleRow countryCode={LOCALES.CA_ENGLISH.countryCode} text={LOCALES.CA_ENGLISH.text} />
        </Column>
      </MarketplaceBox>
    </Row>
    <Row
      spacing="300"
      widths={[
        '50%',
        '50%',
      ]}
    >
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.teal.backgroundColor} borderColor={LANGUAGE_COLORS.teal.borderColor}>
        <Column>
          <LocaleRow countryCode={LOCALES.US_SPANISH.countryCode} text={LOCALES.US_SPANISH.text} />
        </Column>
      </MarketplaceBox>
      <MarketplaceBox backgroundColor={LANGUAGE_COLORS.orange.backgroundColor} borderColor={LANGUAGE_COLORS.orange.borderColor}>
        <Column>
          <LocaleRow countryCode={LOCALES.CA_FRENCH.countryCode} text={LOCALES.CA_FRENCH.text} />
        </Column>
      </MarketplaceBox>
    </Row>
  </PreviewWrapper>
);
